import React, { useState } from "react";
import ModalForm from "./ModalForm";
import Pagination from "./Pagination";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import PRModal from "../components/Pages/Procurements/PruchaseOrder/PurchaseOrders/Modals/PRModal";
import PendingRequestModal from "../components/Pages/Procurements/PruchaseOrder/ApprovalRequests/PendingRequestModal";
import { useNavigate } from "react-router-dom";
import { EndPoints } from "../utils/apiEndpoints";
import { useUserStore } from "../store/useUserStore";
import { FieldErrors } from "../domain/helpers/Errors";
import { darkModeBackgroud, darkModeBackgroudInputs } from "../utils/constants";
import { createActionTdElements } from "./ActionTdElements";
import { useDarkModeStore } from "../store/useDarkModeStore";
import { useLanguageStore } from "../store/useLanguageStore";
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridRowParams, GridValidRowModel } from "@mui/x-data-grid";
import { ModalInterface, NewAddComponentType, ResultObject } from "./CustomDataGridStyle";

interface TableCustomProps<T extends GridValidRowModel> {
  form: T;
  rows: T[];
  name: string;
  page: number;
  itemId?: string;
  pageSize: number;
  loading?: boolean;
  errors: FieldErrors;
  columns: GridColDef[];
  hideColumns: string[];
  modal: ModalInterface;
  showColumnModal: string[];
  isResetPassword?: boolean;
  checkboxSelection?: boolean;
  columnModalExpand?: string[];
  newAddComponent?: NewAddComponentType;
  addItems: () => void;
  handleFeedback?: () => void;
  handleSave: () => Promise<void>;
  handleReset?: (row: T) => Promise<void>;
  setErrors: React.Dispatch<FieldErrors>;
  setForm: React.Dispatch<React.SetStateAction<T>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  handleDelete?: (id: string, confirmation?: boolean) => void;
  setModal: React.Dispatch<React.SetStateAction<ModalInterface>>;
}

export const CustomDataGrid = <T extends GridValidRowModel>({
  name,
  columns,
  rows,
  form,
  errors,
  modal,
  page,
  pageSize,
  itemId = '',
  hideColumns,
  showColumnModal,
  loading = false,
  isResetPassword = false,
  checkboxSelection = false,
  newAddComponent = undefined,
  setForm,
  setModal,
  addItems,
  setPage,
  setErrors,
  handleSave,
  handleReset,
  setPageSize,
  handleDelete,
  handleFeedback,
}: TableCustomProps<T>) => {

  const navigate = useNavigate();
  const { user } = useUserStore();
  const {
    ASSETS, TICKETS, INCIDENTS, USERS, CUSTOMERS, ADDTICKET, ADDASSET, ADDUSER, ADDINCIDENT,
    PURCHASEORDERS, ADDPURCHASEORDER, ADDCUSTOMERS, RECURRINGTICKETS, SUBTICKET, DOCUMENTTEMPLATE, ADDDOCUMENTTEMPLATE
  } = EndPoints;

  const isEmployee = user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === 'Employee';

  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();

  let initialColumnVisibilityModel: ResultObject =
    hideColumns?.reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {} as ResultObject) || {};

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialColumnVisibilityModel);

  const handleColumnVisibilityModelChange = (
    model: GridColumnVisibilityModel
  ) => { setColumnVisibilityModel(model); };

  const handleEdit = (row: T) => {
    switch (newAddComponent) {
      case "Ticket":
        navigate(`${TICKETS}/edit/${row.id}`);
        break;
      case "RecurringTickets":
        navigate(`${RECURRINGTICKETS}/edit/${row.id}`);
        break;
      case "Assets":
        navigate(`${ASSETS}/edit/${row.id}`);
        break;
      case "PurchaseOrders":
        navigate(`${PURCHASEORDERS}/edit/${row.id}`);
        break;
      case "Incidents":
        navigate(`${INCIDENTS}/edit/${row.id}`);
        break;
      case "Customers":
        navigate(`${CUSTOMERS}/edit/${row.id}`);
        break;
      case "Users":
        navigate(`${USERS}/edit/${row.email}`);
        break;
      case "DocumentTemplates":
        navigate(`${DOCUMENTTEMPLATE}/edit/${row.id}`);
        break;
      default:
        setModal({ ...modal, edit: true });
        setErrors({});
        setForm(row);
        break;
    }
  };

  const handleView = (params: GridRowParams<any>) => {
    if (isResetPassword) {
      // Handle reset password case (if any logic is required here)
      return;
    }

    switch (newAddComponent) {
      case "Ticket":
        navigate(`${TICKETS}/view/${params.row.id}`);
        break;
      case "RecurringTickets":
        navigate(`${RECURRINGTICKETS}/view/${params.row.id}`);
        break;
      case "Assets":
        navigate(`${ASSETS}/view/${params.row.id}`);
        break;
      case "PurchaseOrders":
        navigate(`${PURCHASEORDERS}/view/${params.row.id}`);
        break;
      case "Incidents":
        navigate(`${INCIDENTS}/view/${params.row.id}`);
        break;
      case "Customers":
        navigate(`${CUSTOMERS}/view/${params.row.id}`);
        break;
      case "Users":
        navigate(`${USERS}/view/${params.row.email}`);
        break;
      case "DocumentTemplates":
        navigate(`${DOCUMENTTEMPLATE}/view/${params.row.id}`);
        break;
      default:
        setModal({ ...modal, view: true });
        setForm(params.row);
        break;
    }
  };

  const checkforAddComponent = () => {
    if (newAddComponent) {
      switch (newAddComponent) {
        case "Incidents":
          navigate(ADDINCIDENT);
          break;
        case "PurchaseOrders":
          navigate(ADDPURCHASEORDER);
          break;
        case "Ticket":
          navigate(ADDTICKET);
          break;
        case "Assets":
          navigate(ADDASSET);
          break;
        case "Users":
          navigate(ADDUSER);
          break;
        case "Customers":
          navigate(ADDCUSTOMERS);
          break;
        case "DocumentTemplates":
          navigate(ADDDOCUMENTTEMPLATE);
          break;
        default:
          // Optionally handle an unknown `newAddComponent`
          break;
      }
    }
  };

  const handleFeedbackTicket = (id: string) => {
    setForm({ ...form, id });
    handleFeedback && handleFeedback();
  };

  const columnsWithActions = [
    ...columns,
    createActionTdElements({
      page,
      pageSize,
      newAddComponent,
      isResetPassword,
      handleEdit,
      handleDelete,
      handleReset,
      handleFeedbackTicket,
    }),
  ];

  const btnColor = darkMode ? 'white' : 'var(--color-pink)';

  const style = {
    maxHeight: "70vh",
    height: "70vh",
    background: darkMode ? darkModeBackgroud : "white",
    position: "relative",
    color: darkMode ? "white" : "",
    minHeight: "fit-content",
    width: "100%",
    borderRadius: "0",
    border: darkMode ? "none" : "1px solid rgba(224, 224, 224, 1)",
    "--DataGrid-rowBorderColor": darkMode ? "none" : "rgba(224, 224, 224, 1)",
    "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
      position: "relative",
      color: darkMode ? "white" : "",
      borderTop: darkMode ? "none" : "1px solid var(--rowBorderColor)",
    },
    "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cell": {
      color: darkMode ? "white" : "",
    },
    "& .MuiDataGrid-container--top [role=row]": {
      background: darkMode ? darkModeBackgroudInputs : "#F3F4F6",
    },
    "& .MuiDataGrid-overlay": {
      background: darkMode ? darkModeBackgroud : "var(--color-pink-alert)",
      color: darkMode ? "white" : "#0c5460",
      height: "60px",
    },
    "& .MuiDataGrid-footerContainer": {
      color: darkMode ? "white" : "",
      "& .MuiTablePagination-root": {
        color: darkMode ? "white" : "",
        display: "none",
      },
      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
      {
        color: darkMode ? "white" : "",
      },
    },
    "& .MuiDataGrid-columnHeaders": {
      background: darkMode ? darkModeBackgroud : "white",
    },
    "& .MuiDataGrid-columnHeader": {
      "& .MuiCheckbox-root": {
        color: darkMode ? "white" : "",
      },
      "& .MuiDataGrid-sortIcon": {
        color: darkMode ? "white" : "",
      },
      "& .MuiDataGrid-menuIcon": {
        color: darkMode ? "white" : "",
      },
      "& .MuiDataGrid-menu": {
        backgroundColor: darkMode ? darkModeBackgroud : "white",
        color: darkMode ? "white" : "",
        "& .MuiMenuItem-root": {
          color: darkMode ? "white" : "",
        },
      },
      "& .MuiDataGrid-menuIconButton": {
        color: darkMode ? "white" : "",
      },
    },
    "& .MuiButtonBase-root": {
      color: darkMode ? "white" : "",
    },
    "& .MuiDataGrid-footerContainer .MuiTablePagination-displayedRows": {
      display: "none",
    },
    "& .MuiDataGrid-scrollbar": {
      ariaHidden: "false",
    },
    "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within": {
      outline: "none",
    },
    "& .css-de9k3v-MuiDataGrid-selectedRowCount": {
      visibility: 'hidden',
    },
  };

  return (
    <div
      className="table-custom position-relative"
      style={{ background: darkMode ? darkModeBackgroud : "white" }}
    >
      <div
        className="table-header-custom w-100"
        style={darkMode ? { color: "white", border: "none" } : {}}
      >
        <span>{name}</span>
        {
          newAddComponent === "Users"
            ? <IconButton
              aria-label="add an ticket"
              sx={{ marginLeft: "auto", color: btnColor }}
              onClick={checkforAddComponent}
              title={language['Add']}
            >
              <AddIcon />
            </IconButton>
            : newAddComponent === 'RecurringTickets'
              ? null
              : isResetPassword
                ? null
                : !newAddComponent
                  ? <IconButton
                    aria-label="add an item"
                    sx={{ marginLeft: "auto", color: btnColor }}
                    onClick={addItems}
                    title={language['Add']}
                  >
                    <AddIcon />
                  </IconButton>
                  : (["Ticket", "Customers", "Assets", "SubTicket"].includes(newAddComponent))
                    && !isEmployee
                    ? <IconButton
                      aria-label="add an ticket"
                      sx={{ marginLeft: "auto", color: btnColor }}
                      onClick={checkforAddComponent}
                      title={language['Add']}
                    >
                      <AddIcon />
                    </IconButton>
                    : (["Incidents", "PurchaseOrders", "DocumentTemplates"].includes(newAddComponent))
                      ? <IconButton
                        aria-label="add an Incident"
                        sx={{ marginLeft: "auto", color: btnColor }}
                        onClick={checkforAddComponent}
                        title={language['Add']}
                      >
                        <AddIcon />
                      </IconButton>
                      : null
        }
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        arrayLength={rows?.length ?? 0}
        setPage={setPage}
        setPageSize={setPageSize}
      />
      <DataGrid
        rows={rows}
        getRowId={(row) => row?.id ?? Math.random()}
        sx={style}
        columns={columnsWithActions}
        // initialState={{
        //   pagination: {
        //     paginationModel: { page, pageSize },
        //   },
        // }}
        pageSizeOptions={[10, 20, pageSize]}
        checkboxSelection={checkboxSelection}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        localeText={{ noRowsLabel: language["NoDataAvailable"] }}
        onRowClick={handleView}
        loading={loading}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        getRowClassName={() => "clickable-row"}
        disableColumnFilter
        disableColumnSorting
      />
      {
        (newAddComponent === 'PurchaseRequests' || newAddComponent === 'RequestForProposals' || newAddComponent === 'RequestForQuotations')
          && (modal.edit || modal.view)
          ? <PRModal
            form={form}
            open={modal}
            setOpen={setModal}
            newAddComponent={newAddComponent}
          />
          : newAddComponent === 'PendingRequests'
            && (modal.edit || modal.view)
            ? <PendingRequestModal
              form={form}
              open={modal}
              setForm={setForm}
              setOpen={setModal}
            />
            : (!newAddComponent || newAddComponent === "Users")
              ? <ModalForm
                open={modal}
                columns={columns
                  .filter((col) => showColumnModal.some((item) => item === col.field))
                  .sort(
                    (a, b) =>
                      showColumnModal.indexOf(a.field) -
                      showColumnModal.indexOf(b.field)
                  )}
                form={form}
                errors={errors}
                setForm={setForm}
                setOpen={setModal}
                handleSave={handleSave}
              />
              : null
      }
    </div>
  );
};

export default CustomDataGrid;